<script setup lang="ts">
import { ref, computed, watch, useSlots } from 'vue'
import { useQuasar } from 'quasar'
import { useCrudPatch } from '../composables/useCrudPatch' // Assumed explicit import path

// --- Interfaces & Props ---
interface Props {
  title: string
  columns: any[] // Quasar QTable column definition array
  rowKey?: string
  restData: any // The return object from useRestData()
  createApi?: (params: any, options?: any) => Promise<any>
  updateApi?: (params: any, options?: any) => Promise<any>
  deleteApi?: (params: any, options?: any) => Promise<any>
  defaultItem?: Record<string, any>
  // Feature Flags
  enableSearch?: boolean
  enableFilters?: boolean
  enableMultiSort?: boolean
  enableSelection?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  rowKey: 'id',
  defaultItem: () => ({}),
  enableSearch: true,
  enableFilters: true,
  enableMultiSort: false,
  enableSelection: false
})

const emit = defineEmits(['saved', 'deleted', 'error'])

// --- Quasar & Composables ---
const $q = useQuasar()
const slots = useSlots()
const { beginObserving, generatePatch, cleanupObserver } = useCrudPatch()

// --- Vue slots for extensibility ---
const forwardedTableSlots = computed(() => {
  const internalSlots = [
    'top',
    'top-row',
    'body-cell',
    'body-cell-actions',
    'row-actions',
    'create-form',
    'update-form',
    'filters',
    'bulk-actions'
  ]
  return Object.keys(slots).filter((slotName) => !internalSlots.includes(slotName))
})

// --- Selection state ---
const selected = ref<any[]>([])
const clearSelection = () => {
  selected.value = []
}
watch(() => props.restData.items.value, clearSelection)

// --- Internal State ---
const showCreate = ref(false)
const showUpdate = ref(false)
const showDelete = ref(false)
const isSaving = ref(false)

// Toggle for the advanced column filters
const showAdvancedFilters = ref(false)

// We hold a deep clone of the row being edited/created here
const activeItem = ref<Record<string, any>>({})

// Automatically maximize dialogs on small screens
const maximizeDialog = computed(() => $q.screen.lt.md)

// Inject default columns if none are explicitly provided
const tableColumns = computed(() => {
  if (props.columns && props.columns.length > 0) return props.columns
  return [
    { name: 'id', field: 'id', label: 'ID', align: 'left', sortable: false },
    { name: 'createdAt', field: 'createdAt', label: 'Created At', align: 'left', sortable: false },
    { name: 'updatedAt', field: 'updatedAt', label: 'Updated At', align: 'left', sortable: false },
    { name: 'actions', field: 'actions', label: 'Actions', align: 'right' }
  ]
})

// --- Methods ---

const openCreate = () => {
  activeItem.value = JSON.parse(JSON.stringify(props.defaultItem))
  showCreate.value = true
}

const openUpdate = (row: any) => {
  activeItem.value = JSON.parse(JSON.stringify(row))
  beginObserving(activeItem.value)
  showUpdate.value = true
}

const openDelete = (row: any) => {
  activeItem.value = JSON.parse(JSON.stringify(row))
  showDelete.value = true
}

const closeDialogs = () => {
  showCreate.value = false
  showUpdate.value = false
  showDelete.value = false
  activeItem.value = {}
  cleanupObserver()
}

// --- Quick Filter Click Handler ---
const onCellClick = (col: any, row: any) => {
  if (!props.enableFilters || !showAdvancedFilters.value || col.name === 'actions') return

  // Extract the raw underlying value (bypassing any UI formatters like dates)
  // Quasar allows 'field' to be a string or a function, so we safely evaluate both.
  const rawValue = typeof col.field === 'function' ? col.field(row) : row[col.field]

  // If the cell is empty, don't do anything
  if (rawValue === null || rawValue === undefined || rawValue === '') return

  // Populate the filter and trigger the fetch immediately
  props.restData.activeFilters.value[col.field] = rawValue
  props.restData.applyFilters()

  $q.notify({
    message: `Filtered by ${col.label}`,
    color: 'blue-8',
    position: 'top',
    timeout: 1500,
    icon: 'mdi-filter-check'
  })
}

// --- API Execution ---

const handleCreate = async () => {
  if (!props.createApi) return
  isSaving.value = true

  try {
    const result = await props.createApi({ requestBody: activeItem.value })
    $q.notify({ type: 'positive', message: `${props.title} created successfully.` })
    emit('saved', result)
    props.restData.refresh()
    closeDialogs()
  } catch (error: any) {
    emit('error', error)
    $q.notify({ type: 'negative', message: error.message || 'Failed to create item.' })
  } finally {
    isSaving.value = false
  }
}

const handleUpdate = async () => {
  if (!props.updateApi) return
  isSaving.value = true

  try {
    let patchDocument = generatePatch()

    // fast-json-patch deep-observes the coordinates array inside a GeoJSON Point and
    // emits element-level ops (/location/coordinates/0, /location/coordinates/1).
    // SystemTextJsonPatch cannot apply those to NetTopologySuite's fixed-size
    // Coordinate[] — see CLAUDE.md "JSON Patch and GeoJSON coordinates".
    // Collapse any /location/* ops into a single /location replace instead.
    if (patchDocument.some((op) => op.path.startsWith('/location')) && activeItem.value.location) {
      patchDocument = patchDocument.filter((op) => !op.path.startsWith('/location'))
      patchDocument.push({ op: 'replace', path: '/location', value: activeItem.value.location })
    }

    if (patchDocument.length === 0) {
      $q.notify({ type: 'info', message: 'No changes detected.' })
      closeDialogs()
      return
    }

    const params = {
      id: activeItem.value[props.rowKey],
      operation: patchDocument
    }

    const result = await props.updateApi(params)
    $q.notify({ type: 'positive', message: `${props.title} updated successfully.` })
    emit('saved', result)
    props.restData.refresh()
    closeDialogs()
  } catch (error: any) {
    emit('error', error)
    $q.notify({ type: 'negative', message: error.message || 'Failed to update item.' })
  } finally {
    isSaving.value = false
  }
}

const handleDelete = async () => {
  if (!props.deleteApi) return
  isSaving.value = true

  try {
    await props.deleteApi({ id: activeItem.value[props.rowKey] })
    $q.notify({ type: 'positive', message: `${props.title} deleted successfully.` })
    emit('deleted', activeItem.value)
    props.restData.refresh()
    closeDialogs()
  } catch (error: any) {
    emit('error', error)
    $q.notify({ type: 'negative', message: error.message || 'Failed to delete item.' })
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <div>
    <q-banner
      v-if="enableSelection && selected.length > 0"
      inline-actions
      dense
      class="bg-blue-1 q-mb-xs text-primary"
    >
      <template #avatar>
        <q-icon name="mdi-checkbox-multiple-marked" color="primary" />
      </template>
      {{ selected.length }} item{{ selected.length !== 1 ? 's' : '' }} selected
      <template #action>
        <slot name="bulk-actions" :selected="selected" :clear-selection="clearSelection" />
        <q-btn flat dense round icon="mdi-close" @click="clearSelection">
          <q-tooltip>Clear selection</q-tooltip>
        </q-btn>
      </template>
    </q-banner>

    <q-table
      :rows="restData.items.value"
      :columns="tableColumns"
      :row-key="rowKey"
      :loading="restData.isLoading.value"
      :rows-per-page-options="[10, 25, 50, 100, 250, 500]"
      flat
      bordered
      class="bg-white"
      v-model:pagination="restData.pagination.value"
      @request="restData.onRequest"
      :binary-state-sort="true"
      :multi-sort="enableMultiSort"
      :selection="enableSelection ? 'multiple' : 'none'"
      v-model:selected="selected"
    >
      <template v-for="slotName in forwardedTableSlots" :key="slotName" v-slot:[slotName]="scope">
        <slot :name="slotName" v-bind="scope || {}"></slot>
      </template>

      <template v-slot:top>
        <div class="text-h6 q-mr-md">{{ title }}</div>

        <q-input
          v-if="enableSearch"
          v-model="restData.searchQuery.value"
          dense
          outlined
          clearable
          placeholder="Search..."
          class="q-ml-md"
          style="min-width: 250px"
          @keyup.enter="restData.applyFilters()"
          @clear="restData.applyFilters()"
        >
          <template v-slot:append>
            <q-icon name="mdi-magnify" />
          </template>
        </q-input>

        <q-btn
          v-if="enableFilters"
          flat
          dense
          color="primary"
          class="q-ml-sm"
          :icon="showAdvancedFilters ? 'mdi-filter-minus' : 'mdi-filter-plus'"
          @click="showAdvancedFilters = !showAdvancedFilters"
        >
          <q-tooltip>Toggle Advanced Filters</q-tooltip>
        </q-btn>

        <slot name="filters"></slot>

        <q-space />

        <q-btn
          v-if="createApi"
          color="blue-8"
          unelevated
          icon="mdi-plus"
          :label="`New ${title}`"
          @click="openCreate"
        />
      </template>

      <template v-slot:top-row="props" v-if="enableFilters && showAdvancedFilters">
        <q-tr>
          <q-td v-for="col in props.cols" :key="col.name" :class="col.classes" :style="col.style">
            <template v-if="col.name !== 'actions'">
              <q-input
                v-model="restData.activeFilters.value[col.field]"
                dense
                outlined
                clearable
                :placeholder="`Filter ${col.label}`"
                @keyup.enter="restData.applyFilters()"
                @clear="restData.applyFilters()"
              />
            </template>
          </q-td>
        </q-tr>
      </template>

      <template v-slot:body-cell="scope">
        <q-td
          :props="scope"
          @click="onCellClick(scope.col, scope.row)"
          :class="{ 'cursor-pointer': enableFilters && showAdvancedFilters }"
        >
          <template v-if="enableFilters && showAdvancedFilters">
            <q-tooltip>Click to filter by this value</q-tooltip>
          </template>
          {{ scope.value }}
        </q-td>
      </template>

      <template v-slot:body-cell-actions="scope">
        <q-td :props="scope">
          <slot name="row-actions" :row="scope.row">
            <q-btn
              v-if="updateApi"
              flat
              dense
              color="primary"
              icon="mdi-pencil"
              @click="openUpdate(scope.row)"
            >
              <q-tooltip>Edit</q-tooltip>
            </q-btn>
            <q-btn
              v-if="deleteApi"
              flat
              dense
              color="negative"
              icon="mdi-delete"
              @click="openDelete(scope.row)"
            >
              <q-tooltip>Delete</q-tooltip>
            </q-btn>
          </slot>
        </q-td>
      </template>
    </q-table>

    <q-dialog v-model="showCreate" persistent :maximized="maximizeDialog">
      <q-card style="min-width: 400px; max-width: 800px">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Create {{ title }}</div>
          <q-space />
          <q-btn icon="mdi-close" flat round dense v-close-popup @click="closeDialogs" />
        </q-card-section>

        <q-card-section>
          <slot name="create-form" :item="activeItem" :is-saving="isSaving"></slot>
        </q-card-section>

        <q-card-actions align="right" class="q-pa-md">
          <q-btn flat label="Cancel" color="primary" @click="closeDialogs" :disable="isSaving" />
          <q-btn unelevated label="Save" color="blue-8" @click="handleCreate" :loading="isSaving" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="showUpdate" persistent :maximized="maximizeDialog">
      <q-card style="min-width: 400px; max-width: 800px">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Edit {{ title }}</div>
          <q-space />
          <q-btn icon="mdi-close" flat round dense v-close-popup @click="closeDialogs" />
        </q-card-section>

        <q-card-section>
          <slot name="update-form" :item="activeItem" :is-saving="isSaving"></slot>
        </q-card-section>

        <q-card-actions align="right" class="q-pa-md">
          <q-btn flat label="Cancel" color="primary" @click="closeDialogs" :disable="isSaving" />
          <q-btn
            unelevated
            label="Save Changes"
            color="blue-8"
            @click="handleUpdate"
            :loading="isSaving"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="showDelete" persistent>
      <q-card>
        <q-card-section class="row items-center">
          <q-avatar icon="mdi-alert" color="negative" text-color="white" />
          <span class="q-ml-sm"
            >Are you sure you want to delete this {{ title }}? This cannot be undone.</span
          >
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            flat
            label="Cancel"
            color="primary"
            v-close-popup
            @click="closeDialogs"
            :disable="isSaving"
          />
          <q-btn
            unelevated
            label="Delete"
            color="negative"
            @click="handleDelete"
            :loading="isSaving"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<style scoped></style>
